import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import axios from 'axios';
import {
  X,
  TrendingUp,
  Calendar,
  AlertCircle,
  CheckCircle,
  Clock,
  BarChart3
} from 'lucide-react';
import CompanyLogo from '../../common/CompanyLogo';
import { API_URL } from '../../../config/api';
import { calculateValuationPeriod } from '../../../utils/valuationPeriodCalculator';

interface ExerciseNoticeModalProps {
  isOpen: boolean;
  onClose: () => void;
  facilityId: string;
  availableAmount: number;
  currentPrice: number;
  ticker: string;
  companyName: string;
  vwapPercent: number;
  rapidPercent: number;
  commitmentAmount?: number; // Add commitment amount
  fiveDayAvgVolume?: number; // Add 5-day average volume
  outstandingShares?: number; // Add outstanding shares for 4.99% cap
  onExerciseSubmitted: () => void;
  isDemoMode?: boolean; // Add demo mode flag
  onDemoComplete?: () => void; // Add demo completion callback
}

const ExerciseNoticeModal: React.FC<ExerciseNoticeModalProps> = ({
  isOpen,
  onClose,
  facilityId,
  availableAmount,
  currentPrice,
  ticker,
  companyName,
  vwapPercent,
  rapidPercent,
  commitmentAmount = availableAmount, // Default to available amount if not provided
  fiveDayAvgVolume = 1000000, // Default volume if not provided
  outstandingShares = 0, // Default outstanding shares if not provided
  onExerciseSubmitted,
  isDemoMode = false,
  onDemoComplete
}) => {
  const [exerciseType, setExerciseType] = useState<'VWAP' | 'RAPID'>('VWAP');
  const [shares, setShares] = useState<number>(0);
  const [dollarAmount, setDollarAmount] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [showSignature, setShowSignature] = useState(false);
  const [signingUrl, setSigningUrl] = useState<string>('');
  const [envelopeId, setEnvelopeId] = useState<string>('');
  const [transactionId, setTransactionId] = useState<string>('');
  const [signingComplete, setSigningComplete] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [showDemoComplete, setShowDemoComplete] = useState(false);

  // Calculate maximum shares based on exercise type
  const calculateMaxShares = () => {
    // Volume limit based on exercise type - convert percentage to decimal
    const volumePercentage = exerciseType === 'VWAP' ? (vwapPercent / 100) : (rapidPercent / 100);
    const volumeLimitShares = Math.floor(fiveDayAvgVolume * volumePercentage);
    
    // Commitment limit (10% of commitment amount)
    const commitmentLimitShares = Math.floor(commitmentAmount * 0.10 / currentPrice);
    
    // Outstanding shares limit (4.99% of outstanding)
    const outstandingLimitShares = outstandingShares > 0 
      ? Math.floor(outstandingShares * 0.0499)
      : Infinity;
    
    // Return the lowest of all limits
    return Math.min(volumeLimitShares, commitmentLimitShares, outstandingLimitShares);
  };

  const maxShares = calculateMaxShares();
  const maxDollarAmount = maxShares * currentPrice;

  // Get dynamic valuation period information
  const valuationInfo = calculateValuationPeriod(exerciseType);

  // Update dollar amount when shares change
  useEffect(() => {
    setDollarAmount(shares * currentPrice);
  }, [shares, currentPrice]);

  // Reset shares when exercise type changes
  useEffect(() => {
    setShares(0);
    setError('');
  }, [exerciseType]);

  const handleSharesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || 0;
    setShares(value);
    setError('');
  };

  const validateAmount = (): boolean => {
    if (shares <= 0) {
      setError('Please enter a valid number of shares');
      return false;
    }
    
    if (shares > maxShares) {
      setError(`Amount exceeds maximum allowed shares of ${maxShares.toLocaleString()}`);
      return false;
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!validateAmount()) return;
    
    setLoading(true);
    setError('');
    
    try {
      if (isDemoMode) {
        // Demo mode - just show completion
        setShowDemoComplete(true);
        if (onDemoComplete) {
          onDemoComplete();
        }
        return;
      }

      // Real submission
      const token = localStorage.getItem('token');
      
      // Step 1: Create the exercise notice transaction
      console.log('📝 Creating exercise notice transaction...');
      const exerciseResponse = await axios.post(
        `${API_URL}/api/transactions/exercise-notice`,
        {
          facilityId,
          transactionType: exerciseType,
          sharesRequested: shares,
          exerciseTime: new Date().toISOString()
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (exerciseResponse.data.success) {
        const transactionId = exerciseResponse.data.data.transactionId;
        console.log('✅ Exercise notice transaction created:', transactionId);
        
        // Step 2: Create SignNow document for signing
        console.log('📤 Creating SignNow document...');
        const signNowResponse = await axios.post(
          `${API_URL}/api/signature/exercise-notice/${transactionId}`,
          {},
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (signNowResponse.data.success) {
          // Show signature flow
          setShowSignature(true);
          setSigningUrl(signNowResponse.data.data.signingUrl || '#');
          setEnvelopeId(signNowResponse.data.data.documentId || '');
          setTransactionId(transactionId);
          console.log('✅ SignNow document created successfully');
        } else {
          setError(signNowResponse.data.message || 'Failed to create signing document');
        }
      } else {
        setError(exerciseResponse.data.message || 'Failed to submit exercise notice');
      }
    } catch (err: any) {
      console.error('Error submitting exercise notice:', err);
      
      // Provide specific error messages based on status code
      if (err.response?.status === 401) {
        setError('Session expired. Please refresh the page and log in again.');
      } else if (err.response?.status === 400) {
        setError(err.response.data?.message || 'Invalid exercise parameters. Please check your input.');
      } else if (err.response?.status === 404) {
        setError('Facility not found. Please ensure you have an active facility.');
      } else if (err.response?.status === 500) {
        setError('Server error. Please try again in a moment.');
      } else if (err.code === 'NETWORK_ERROR' || !err.response) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError(err.response?.data?.message || err.message || 'An unexpected error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  // Polling effect to check signing status when showing signature interface
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (showSignature && transactionId && !signingComplete) {
      // Start polling every 10 seconds
      interval = setInterval(async () => {
        try {
          setCheckingStatus(true);
          const token = localStorage.getItem('token');
          const statusResponse = await axios.get(
            `${API_URL}/api/signature/exercise-notice-status/${transactionId}`,
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );
          
          if (statusResponse.data.success && statusResponse.data.data.isComplete) {
            console.log('✅ Exercise notice signing completed!');
            setSigningComplete(true);
            clearInterval(interval);
            
            // Automatically close after a short delay
            setTimeout(() => {
              handleSigningComplete();
            }, 2000);
          }
        } catch (error) {
          console.log('Status check error (normal):', error);
        } finally {
          setCheckingStatus(false);
        }
      }, 10000); // Check every 10 seconds
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [showSignature, transactionId, signingComplete]);

  const handleSigningComplete = () => {
    onExerciseSubmitted();
    onClose();
  };

  if (showSignature && signingUrl) {
    return (
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => {}}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 flex justify-between items-center"
                  >
                    <div className="flex items-center gap-2">
                      Sign Exercise Notice
                      {checkingStatus && (
                        <div className="flex items-center gap-1 text-sm text-blue-600">
                          <Clock className="h-4 w-4 animate-spin" />
                          Checking status...
                        </div>
                      )}
                      {signingComplete && (
                        <div className="flex items-center gap-1 text-sm text-green-600">
                          <CheckCircle className="h-4 w-4" />
                          Signed successfully!
                        </div>
                      )}
                    </div>
                    <button
                      onClick={handleSigningComplete}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </Dialog.Title>
                  
                  {signingComplete ? (
                    <div className="mt-4 text-center py-20">
                      <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Exercise Notice Signed Successfully!</h3>
                      <p className="text-sm text-gray-600 mb-4">
                        Your exercise notice has been signed and submitted. You can now close this window.
                      </p>
                      <button
                        onClick={handleSigningComplete}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                      >
                        Complete
                      </button>
                    </div>
                  ) : (
                    <div className="mt-4" style={{ height: '600px' }}>
                      <iframe
                        src={signingUrl}
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        title="Sign Exercise Notice"
                      />
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    );
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
                {showDemoComplete ? (
                  // Demo completion message
                  <div className="p-6 text-center">
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 mb-4">
                      <CheckCircle className="h-6 w-6 text-green-600" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      That's it! Equity Finance Made Simple.
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Now proceed to your Pre-Closing Checklist where you'll complete the final steps in establishing your new equity facility.
                    </p>
                    <p className="text-sm text-gray-600">
                      Please don't hesitate to reach out in the event you have any questions or we can be of assistance.
                    </p>
                  </div>
                ) : (
                  // Regular modal content
                  <>
                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 flex justify-between items-center"
                  >
                    Submit Exercise Notice
                    <button
                      onClick={onClose}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </Dialog.Title>

                  <div className="mt-4 space-y-4">
                    {/* Company Info */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CompanyLogo 
                          ticker={ticker} 
                          companyName={companyName} 
                          size="md" 
                        />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {companyName} ({ticker})
                          </div>
                          <div className="text-sm text-gray-500">
                            Current Price: ${currentPrice.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Exercise Type Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Exercise Type
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => setExerciseType('VWAP')}
                          className={`p-3 rounded-lg border-2 transition-colors ${
                            exerciseType === 'VWAP'
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <TrendingUp className="h-5 w-5 mx-auto mb-1" />
                          <div className="font-medium">VWAP</div>
                          <div className="text-xs text-gray-500">{vwapPercent}% of volume</div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setExerciseType('RAPID')}
                          className={`p-3 rounded-lg border-2 transition-colors ${
                            exerciseType === 'RAPID'
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <Calendar className="h-5 w-5 mx-auto mb-1" />
                          <div className="font-medium">Rapid</div>
                          <div className="text-xs text-gray-500">{rapidPercent}% of volume</div>
                        </button>
                      </div>
                    </div>

                    {/* Valuation Period Information */}
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <div className="flex items-start gap-2">
                        <Clock className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-blue-800">
                          <div className="font-medium mb-1">{valuationInfo.valuationPeriod}</div>
                          <div className="mb-1">{valuationInfo.description}</div>
                          <div className="font-medium">Settlement Period: {valuationInfo.settlementPeriod}</div>
                        </div>
                      </div>
                    </div>

                    {/* Volume Threshold Information */}
                    <div className="bg-green-50 p-3 rounded-lg">
                      <div className="flex items-start gap-2">
                        <BarChart3 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-green-800">
                          <div className="font-medium mb-1">Volume Threshold Monitoring</div>
                          <div>Volume is logged at exercise and updated every 15 minutes. When 10% of total volume equals or exceeds requested shares, the Valuation Period concludes immediately, allowing additional exercise notices.</div>
                        </div>
                      </div>
                    </div>

                    {/* Shares Input */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Number of Shares
                      </label>
                      <div className="space-y-3">
                        {/* Slider */}
                        <input
                          type="range"
                          value={shares}
                          onChange={handleSharesChange}
                          min="0"
                          max={maxShares}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                        />
                        
                        {/* Number Input */}
                        <div className="flex items-center gap-4">
                          <input
                            type="number"
                            value={shares}
                            onChange={handleSharesChange}
                            placeholder="0"
                            className="block w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-right"
                            min="0"
                            max={maxShares}
                          />
                          <span className="text-sm text-gray-600">shares</span>
                        </div>
                        
                        {/* Dynamic Value Display */}
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Estimated Value:</span>
                            <span className="text-lg font-semibold text-gray-900">
                              ${(shares * currentPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            *Estimated value based on current share price. Final pricing is calculated upon completion of the valuation period.
                          </p>
                        </div>
                        
                        {/* Limits Display */}
                        <div className="text-xs text-gray-500 space-y-1">
                          <div>Max shares: {maxShares.toLocaleString()}</div>
                          <div>Available facility: ${availableAmount.toLocaleString()}</div>
                          {outstandingShares > 0 && (
                            <div>4.99% of outstanding: {Math.floor(outstandingShares * 0.0499).toLocaleString()} shares</div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Info Section */}
                    <div className="bg-gray-50 p-3 rounded-lg flex items-start">
                      <AlertCircle className="h-5 w-5 text-gray-400 mr-2 flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-gray-600">
                        {exerciseType === 'VWAP' 
                          ? `A VWAP Purchase uses a 3-day Valuation Period while allowing a larger purchase amount. The Maximum Purchase is the lesser of (i) ${vwapPercent}% of the 5-day average volume, or (ii) 10% of the Commitment Amount. Not to exceed 4.99% of the Outstanding Shares per exercise notice.`
                          : `A Rapid Purchase uses a 1-day Valuation Period for faster settlement. The Maximum Purchase is the lesser of (i) ${rapidPercent}% of the 5-day average volume, or (ii) 10% of the Commitment Amount. Not to exceed 4.99% of the Outstanding Shares per exercise notice.`
                        }
                      </div>
                    </div>

                    {/* Error Message */}
                    {error && (
                      <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
                        {error}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-end space-x-3 pt-4">
                      <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        disabled={loading}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={loading || shares <= 0}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Processing...' : isDemoMode ? 'Complete Demo' : 'Submit & Sign'}
                      </button>
                    </div>
                  </div>
                </div>
                   </>
                 )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ExerciseNoticeModal; 